import { View, Platform, ScrollView, TouchableOpacity, Alert, Image, TextInput } from 'react-native';
import React from 'react';
import DefaultScreen from '@components/Global/DefaultScreenView';
import MenuIconCard from '@components/Global/MenuIconCard';
import AnimatedLottieView from '@utils/lottieAnimation';
import { useQuery } from '@tanstack/react-query';
import apis from '@api/api';
import { useNavigation } from '@react-navigation/native';
import { userAuths } from '@reduxLocal/selectors/userSelectors';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { H5, XStack, Text, H6, YStack, Button, Dialog } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import CustomDialog from '@components/UI/CustomDialog';
import ImagePickerComponent from '@components/formComponents/ImageSelection';
import ColorPicker from '@hooks/subcomponents/ColorPicker';

const DataTemplates = () => {
  const userAuthentications = userAuths();
  const navigation = useNavigation();
  const theme = themeSelector();
  const [headerOpen, setHeaderOpen] = React.useState(true);
  const [brandingDialogOpen, setBrandingDialogOpen] = React.useState(false);
  const [brandingData, setBrandingData] = React.useState<any>({});
  const [primaryColor, setPrimaryColor] = React.useState('#1E90FF');
  const [secondaryColor, setSecondaryColor] = React.useState('#FF6B6B');
  const [companyDescription, setCompanyDescription] = React.useState('');
  const [logoUrl, setLogoUrl] = React.useState('');
  const [loadingBranding, setLoadingBranding] = React.useState(false);


  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['dataTemplateCount', userAuthentications?.id], // Include user ID to invalidate when user changes
    queryFn: async () => {
      try {

        const result = await apis.getDataTemplateCount();

        // Validate the response structure and ensure all expected fields exist
        if (!result || typeof result !== 'object') {
          console.warn('⚠️ Invalid API response structure:', result);
          return { email: 0, invoice: 0, document: 0, models: 0, utilities: 0 };
        }

        // Ensure all counts are numbers and not undefined/null
        const normalizedResult = {
          email: Number(result.email) || 0,
          invoice: Number(result.invoice) || 0,
          document: Number(result.document) || 0,
          models: Number(result.models) || 0,
          utilities: Number(result.utilities) || 0,
        };

        console.log('🔄 Normalized result:', normalizedResult);
        return normalizedResult;
      } catch (apiError) {
        console.error('❌ Template count API error:', apiError);
        console.error('📝 Error details:', {
          message: apiError.message,
          stack: apiError.stack,
          response: apiError.response?.data,
          status: apiError.response?.status
        });
        throw apiError;
      }
    },
    // Remove initialData to allow proper loading state
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 30000, // Consider data stale after 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes (formerly cacheTime)
    retry: (failureCount, error) => {
      console.log(`🔄 Retry attempt ${failureCount} for template count:`, error);
      return failureCount < 3;
    },
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Debug logging - Enhanced with authentication status
  console.log('📈 Current data state:', data);
  console.log('❌ Any errors:', error);
  console.log('⏳ Is loading:', isLoading);
  console.log('🔐 User authentication status:', {
    isAuthenticated: !!userAuthentications,
    userEmail: userAuthentications?.email,
    userId: userAuthentications?.id,
    hasToken: !!userAuthentications?.token
  });
  console.log('🌍 Environment check:', {
    platform: Platform.OS,
    apiUrl: process.env.EXPO_PUBLIC_API_URL,
    webApiUrl: process.env.EXPO_PUBLIC_WEB_API
  });
  console.log('🔢 Email count for MenuIconCard:', data?.email, 'Should show:', data?.email > 0 ? data?.email : undefined);
  console.log('🧾 Invoice count for MenuIconCard:', data?.invoice, 'Should show:', data?.invoice > 0 ? data?.invoice : undefined);
  console.log('📄 Document count for MenuIconCard:', data?.document, 'Should show:', data?.document > 0 ? data?.document : undefined);

  // Calculate totals - use fallback values if data is not loaded yet
  const safeData = data || { email: 0, invoice: 0, document: 0, models: 0, utilities: 0 };
  const totalTemplates = Object.values(safeData).reduce((sum: number, count) => sum + (count as number || 0), 0);
  const activeCategories = Object.values(safeData).filter(count => (count as number) > 0).length;
  const mostUsedCategory = Object.entries(safeData).reduce((max, [key, value]) => 
    (value as number) > (max.count as number) ? { name: key, count: value as number } : max, 
    { name: 'email', count: 0 }
  );

  // Prepare data for the chart
  const chartLabels = Object.keys(safeData)
    .filter(key => safeData[key] > 0)
    .map(key => key.charAt(0).toUpperCase() + key.slice(1));

  const chartDataPoints = Object.values(safeData).filter(value => (value as number) > 0) as number[];

  // Fetch branding data
  const { data: brandingResponse } = useQuery({
    queryKey: ['userBranding', userAuthentications?.id],
    queryFn: async () => {
      try {
        const response = await apis.getUserBranding();
        if (response.success && response.data) {
          setBrandingData(response.data);
          setPrimaryColor(response.data.colors?.primary || '#1E90FF');
          setSecondaryColor(response.data.colors?.secondary || '#FF6B6B');
          setCompanyDescription(response.data.companyName || response.data.description || '');
          setLogoUrl(response.data.logo?.url || '');
        }
        return response;
      } catch (error) {
        console.error('Error loading branding data:', error);
        return null;
      }
    },
    enabled: !!userAuthentications?.id,
    staleTime: 300000, // 5 minutes
  });

  const handleLogoUpload = async (images: any[]) => {
    if (images && images.length > 0) {
      try {
        setLoadingBranding(true);
        const image = images[0];

        // Use the image URI directly (the API will handle the upload)
        const response = await apis.uploadBrandingImage(
          'logo',
          image.uri,
          image.fileName || 'logo.png',
          image.type || 'image/png'
        );

        if (response.success) {
          const updatedBranding = response.data.branding || response.data;
          setBrandingData(updatedBranding);
          setLogoUrl(updatedBranding.logo?.url || '');
          Alert.alert('Success', 'Logo uploaded successfully');
        }
      } catch (error) {
        console.error('Error uploading logo:', error);
        Alert.alert('Error', 'Failed to upload logo');
      } finally {
        setLoadingBranding(false);
      }
    }
  };

  const handleSaveBranding = async () => {
    try {
      setLoadingBranding(true);
      const updatedBranding = {
        ...brandingData,
        companyName: companyDescription,
        description: companyDescription,
        colors: {
          ...brandingData.colors,
          primary: primaryColor,
          secondary: secondaryColor,
        }
      };

      const response = await apis.updateUserBranding(updatedBranding);
      if (response.success) {
        setBrandingData(response.data);
        Alert.alert('Success', 'Branding settings saved successfully');
        setBrandingDialogOpen(false);
      }
    } catch (error) {
      console.error('Error saving branding data:', error);
      Alert.alert('Error', 'Failed to save branding settings');
    } finally {
      setLoadingBranding(false);
    }
  };

  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color);
  };

  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color);
  };

  return (
    // Use the 'scrollable' prop on DefaultScreen to allow the page to scroll
    <DefaultScreen style={{ paddingLeft: 2, paddingRight: 3 }} backButton>
      {/* Header with Template Insights */}
      <Animatable.View
        animation="fadeInDown"
        duration={800}
        style={{
          backgroundColor: theme ? "#ffffff" : "#1e293b",
          borderRadius: 20,
          padding: 24,
          marginHorizontal: Platform.OS === 'web' ? "5%" : 10,
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: theme ? 0.1 : 0.3,
          shadowRadius: 20,
          elevation: 10,
          borderWidth: 1,
          borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <View style={{
              backgroundColor: theme ? "#f0f9ff" : "#0c4a6e",
              borderRadius: 12,
              padding: 12,
              marginRight: 16
            }}>
              <MaterialIcons
                name="library-books"
                size={24}
                color={theme ? "#0ea5e9" : "#38bdf8"}
              />
            </View>
            <View style={{ flex: 1 }}>
              <H6 style={{
                color: theme ? "#1e293b" : "#f1f5f9",
                fontWeight: '700',
                marginBottom: 4
              }}>
                Template Library
              </H6>
              <Text style={{
                color: theme ? "#64748b" : "#94a3b8",
                fontSize: 14
              }}>
                Manage and track all your content templates
              </Text>
            </View>
          </View>

          <TouchableOpacity
            onPress={() => setHeaderOpen(!headerOpen)}
            style={{ padding: 8 }}
          >
            <MaterialIcons
              name={headerOpen ? "keyboard-arrow-up" : "keyboard-arrow-down"}
              size={28}
              color={theme ? '#64748b' : '#94a3b8'}
            />
          </TouchableOpacity>
        </View>

        {headerOpen && (
          <Animatable.View
            animation="fadeInDown"
            duration={400}
            style={{ paddingVertical: 10 }}
          >

        {/* Debug Panel - Show API status and manual test button */}
        {/* {__DEV__ && (
          <View style={{
            backgroundColor: theme ? "#fef3c7" : "#92400e",
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            borderWidth: 1,
            borderColor: theme ? "#f59e0b" : "#d97706"
          }}>
            <Text style={{
              color: theme ? "#92400e" : "#fef3c7",
              fontWeight: '600',
              marginBottom: 8
            }}>
              🔧 Debug Panel
            </Text>
            <Text style={{
              color: theme ? "#92400e" : "#fef3c7",
              fontSize: 12,
              marginBottom: 8
            }}>
              Loading: {isLoading ? 'Yes' : 'No'} | Error: {error ? 'Yes' : 'No'} | Data Keys: {Object.keys(data || {}).join(', ')}
            </Text>
            <XStack gap={8} flexWrap="wrap">
              <TouchableOpacity
                onPress={testApiCall}
                style={{
                  backgroundColor: theme ? "#f59e0b" : "#fef3c7",
                  borderRadius: 8,
                  padding: 10,
                  flex: 1,
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: theme ? "#ffffff" : "#92400e",
                  fontWeight: '600',
                  fontSize: 12
                }}>
                  🧪 Test API
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => refetch()}
                disabled={isLoading}
                style={{
                  backgroundColor: isLoading 
                    ? (theme ? "#d1d5db" : "#4b5563") 
                    : (theme ? "#10b981" : "#065f46"),
                  borderRadius: 8,
                  padding: 10,
                  flex: 1,
                  alignItems: 'center'
                }}
              >
                <Text style={{
                  color: isLoading 
                    ? (theme ? "#6b7280" : "#9ca3af") 
                    : "#ffffff",
                  fontWeight: '600',
                  fontSize: 12
                }}>
                  {isLoading ? '⏳ Loading' : '🔄 Refetch'}
                </Text>
              </TouchableOpacity>
            </XStack>
          </View>
        )} */}

        {/* Error Display */}
        {error && (
          <View style={{
            backgroundColor: theme ? "#fef2f2" : "#7f1d1d",
            borderRadius: 12,
            padding: 16,
            marginBottom: 20,
            borderWidth: 1,
            borderColor: theme ? "#ef4444" : "#dc2626"
          }}>
            <Text style={{
              color: theme ? "#dc2626" : "#fef2f2",
              fontWeight: '600',
              marginBottom: 8
            }}>
              ❌ API Error Detected
            </Text>
            <Text style={{
              color: theme ? "#dc2626" : "#fef2f2",
              fontSize: 12
            }}>
              {error.message}
            </Text>
          </View>
        )}

        {/* Stats Grid */}
        {isLoading ? (
          <View style={{ alignItems: 'center', justifyContent: 'center', height: 100 }}>
            <AnimatedLottieView source={require('@assets/animations/leaf_Loader.json')} style={{ width: 60, height: 60 }} autoPlay loop />
            <Text style={{
              color: theme ? "#64748b" : "#94a3b8",
              fontSize: 14,
              marginTop: 8
            }}>
              Loading template counts...
            </Text>
          </View>
        ) : (
        <XStack gap={12} flexWrap="wrap" justifyContent="space-between">
          {[
            {
              title: 'Total Templates',
              value: totalTemplates.toString(),
              icon: 'description',
              color: theme ? "#0ea5e9" : "#38bdf8",
              bgColor: theme ? "#dbeafe" : "#0c4a6e"
            },
            {
              title: 'Active Categories',
              value: activeCategories.toString(),
              icon: 'category',
              color: theme ? "#059669" : "#10b981",
              bgColor: theme ? "#d1fae5" : "#064e3b"
            },
            {
              title: 'Most Used',
              value: mostUsedCategory.name.charAt(0).toUpperCase() + mostUsedCategory.name.slice(1),
              icon: 'trending-up',
              color: theme ? "#7c3aed" : "#a78bfa",
              bgColor: theme ? "#ede9fe" : "#312e81"
            },
            {
              title: 'Email Templates',
              value: (safeData?.email || 0).toString(),
              icon: 'email',
              color: theme ? "#f59e0b" : "#fbbf24",
              bgColor: theme ? "#fef3c7" : "#451a03"
            },
            {
              title: 'Invoice Templates',
              value: (safeData?.invoice || 0).toString(),
              icon: 'receipt',
              color: theme ? "#ef4444" : "#f87171",
              bgColor: theme ? "#fee2e2" : "#7f1d1d"
            },
            {
              title: 'Document Templates',
              value: (safeData?.document || 0).toString(),
              icon: 'insert-drive-file',
              color: theme ? "#8b5cf6" : "#a78bfa",
              bgColor: theme ? "#f3e8ff" : "#3c1361"
            }
          ].map((stat, index) => (
            <Animatable.View
              key={index}
              animation="bounceIn"
              delay={index * 100}
              style={{
                flex: 1,
                minWidth: 140,
                backgroundColor: stat.bgColor,
                borderRadius: 16,
                padding: 16,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
              }}
            >
              <MaterialIcons
                name={stat.icon as any}
                size={20}
                color={stat.color}
                style={{ marginBottom: 6 }}
              />
              <Text style={{
                fontSize: 18,
                fontWeight: '700',
                color: stat.color,
                marginBottom: 2
              }}>
                {stat.value}
              </Text>
              <Text style={{
                fontSize: 11,
                fontWeight: '600',
                color: theme ? "#64748b" : "#94a3b8",
                textAlign: 'center'
              }}>
                {stat.title}
              </Text>
            </Animatable.View>
          ))}
        </XStack>
        )}
          </Animatable.View>
        )}
      </Animatable.View>
      <XStack flexWrap='wrap' gap={20} columnGap={20} style={{ gap: 5, marginBottom: 150, alignSelf: 'stretch', flexDirection: 'row', flexWrap: 'wrap' }}>
            <MenuIconCard minWidth={400} count={safeData?.email > 0 ? safeData?.email : undefined} pressFunction={() => (navigation as any).navigate("EmailTemplates")} subtitle='Your email templates can be found here, ready to be used' cardTitle={"Email Templates"}>
            <AnimatedLottieView source={require('@assets/animations/email.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>

            <MenuIconCard minWidth={400} count={safeData?.invoice > 0 ? safeData?.invoice : undefined} pressFunction={() => (navigation as any).navigate("InvoiceTemplates")} subtitle='Your invoice templates can be found here' cardTitle={"Invoice Templates"}>
            <AnimatedLottieView source={require('@assets/animations/invoice.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>

            <MenuIconCard minWidth={400} count={safeData?.document > 0 ? safeData?.document : undefined} pressFunction={() => (navigation as any).navigate("DocumentTemplates")} subtitle='Your document/contract templates can be found here' cardTitle={"Document Templates"}>
            <AnimatedLottieView source={require('@assets/animations/sign.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center', padding: 5 }} autoPlay />
          </MenuIconCard>
          <MenuIconCard minWidth={400} count={undefined} pressFunction={() => (navigation as any).navigate("CreativeTemplates")} subtitle='Templates used in your ad creatives' cardTitle={"Creative Templates"}>
            <AnimatedLottieView source={require('@assets/animations/127379-create-post.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>
          <MenuIconCard minWidth={400} count={undefined} pressFunction={() => {
            alert('Coming soon')
          }} subtitle='Build custom webpages using drag and drop and AI' cardTitle={"Webpage Templates"}>
            <AnimatedLottieView source={require('@assets/animations/web.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center' }} autoPlay />
          </MenuIconCard>
            <MenuIconCard minWidth={400} count={safeData?.utilities > 0 ? safeData?.utilities : undefined} pressFunction={() => (navigation as any).navigate("UtilityTemplates")} subtitle='Utility templates' cardTitle={"Utilities"}>
            <AnimatedLottieView source={require('@assets/animations/sign.json')} style={{ width: 100, height: 50, alignSelf: 'center', justifyContent: 'center', padding: 5 }} autoPlay />
          </MenuIconCard>
          {
            Object.keys(userAuthentications).includes('whatsapp') &&
            <MenuIconCard minWidth={400}
              count={undefined}
              pressFunction={() => (navigation as any).navigate("WhatsappTemplates")}
              subtitle="Your whatsapp templates can be found here"
              cardTitle={"Whatsapp Templates"}
              >
                <View style={{ alignItems: 'center', justifyContent: 'center', height: 50 }}>
                <Ionicons name="logo-whatsapp" size={40} color="#25D366" />
              </View>
            </MenuIconCard>
          }
          <MenuIconCard minWidth={400}
            count={undefined}
            pressFunction={() => setBrandingDialogOpen(true)}
            subtitle="Customize your company logo, colors, and branding"
            cardTitle={"Branding Settings"}
          >
            <View style={{ alignItems: 'center', justifyContent: 'center', height: 50 }}>
              <MaterialIcons name="palette" size={40} color={primaryColor || (theme ? "#667eea" : "#4a90e2")} />
            </View>
          </MenuIconCard>
      </XStack>

      {/* Branding Settings Dialog */}
      <Dialog modal open={brandingDialogOpen} onOpenChange={setBrandingDialogOpen}>
        <Dialog.Portal>
          <Dialog.Overlay
            animation="quick"
            opacity={0.5}
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Dialog.Content
            bordered
            elevate
            animation={[
              'quick',
              {
                opacity: {
                  overshootClamping: true,
                },
              },
            ]}
            enterStyle={{ x: 0, y: -20, opacity: 0, scale: 0.9 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            gap="$4"
            maxWidth={600}
            width="90%"
            maxHeight="85%"
          >
            <Dialog.Title style={{ color: theme ? "#1e293b" : "#f1f5f9" }}>
              Branding Settings
            </Dialog.Title>
            <ScrollView style={{ maxHeight: 500 }}>
              <YStack gap="$4" padding="$2">
                {/* Company Logo Section */}
                <View>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 12
                  }}>
                    Company Logo
                  </Text>
                  <View style={{
                    backgroundColor: theme ? 'rgba(102, 126, 234, 0.1)' : 'rgba(74, 144, 226, 0.1)',
                    borderRadius: 12,
                    padding: 16,
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 120
                  }}>
                    {logoUrl ? (
                      <XStack alignItems="center" gap="$3" flexWrap="wrap" justifyContent="center">
                        <View style={{
                          width: 80,
                          height: 80,
                          borderRadius: 8,
                          overflow: 'hidden',
                          backgroundColor: theme ? '#f8f9fa' : '#2d3748',
                          marginBottom: 12
                        }}>
                          <Image
                            source={{ uri: logoUrl }}
                            style={{
                              width: '100%',
                              height: '100%',
                            }}
                            resizeMode="contain"
                          />
                        </View>
                        <ImagePickerComponent
                          onImageSelected={handleLogoUpload}
                          selected={true}
                        />
                      </XStack>
                    ) : (
                      <ImagePickerComponent onImageSelected={handleLogoUpload} />
                    )}
                  </View>
                </View>

                {/* Company Description */}
                <View>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 8
                  }}>
                    Company Description
                  </Text>
                  <TextInput
                    value={companyDescription}
                    onChangeText={setCompanyDescription}
                    placeholder="Enter your company description"
                    placeholderTextColor={theme ? "#94a3b8" : "#64748b"}
                    multiline
                    numberOfLines={3}
                    style={{
                      backgroundColor: theme ? "#f8fafc" : "#334155",
                      borderRadius: 8,
                      padding: 12,
                      color: theme ? "#1e293b" : "#f1f5f9",
                      borderWidth: 1,
                      borderColor: theme ? "#e2e8f0" : "#475569",
                      minHeight: 80,
                      textAlignVertical: 'top'
                    }}
                  />
                </View>

                {/* Primary Color */}
                <View>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 12
                  }}>
                    Primary Brand Color
                  </Text>
                  <XStack alignItems="center" gap="$3" flexWrap="wrap">
                    <View style={{
                      width: 50,
                      height: 50,
                      borderRadius: 25,
                      backgroundColor: primaryColor,
                      borderWidth: 3,
                      borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)",
                    }} />
                    <View style={{ flex: 1, minWidth: 200 }}>
                      <ColorPicker
                        color={primaryColor}
                        onColorChange={handlePrimaryColorChange}
                      />
                    </View>
                  </XStack>
                </View>

                {/* Secondary Color */}
                <View>
                  <Text style={{
                    fontSize: 16,
                    fontWeight: '600',
                    color: theme ? "#1e293b" : "#f1f5f9",
                    marginBottom: 12
                  }}>
                    Secondary Brand Color
                  </Text>
                  <XStack alignItems="center" gap="$3" flexWrap="wrap">
                    <View style={{
                      width: 50,
                      height: 50,
                      borderRadius: 25,
                      backgroundColor: secondaryColor,
                      borderWidth: 3,
                      borderColor: theme ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.2)",
                    }} />
                    <View style={{ flex: 1, minWidth: 200 }}>
                      <ColorPicker
                        color={secondaryColor}
                        onColorChange={handleSecondaryColorChange}
                      />
                    </View>
                  </XStack>
                </View>

                {loadingBranding && (
                  <View style={{ alignItems: 'center', padding: 12 }}>
                    <Text style={{ color: theme ? "#64748b" : "#94a3b8" }}>
                      Saving...
                    </Text>
                  </View>
                )}
              </YStack>
            </ScrollView>

            <XStack gap="$3" justifyContent="flex-end" paddingTop="$3">
              <Dialog.Close displayWhenAdapted asChild>
                <Button
                  variant="outlined"
                  onPress={() => setBrandingDialogOpen(false)}
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: theme ? "#e2e8f0" : "#475569",
                    borderWidth: 1,
                  }}
                >
                  <Text style={{ color: theme ? "#64748b" : "#94a3b8" }}>Cancel</Text>
                </Button>
              </Dialog.Close>
              <Button
                onPress={handleSaveBranding}
                disabled={loadingBranding}
                style={{
                  backgroundColor: primaryColor || (theme ? "#667eea" : "#4a90e2"),
                }}
              >
                <Text style={{ color: "#ffffff", fontWeight: '600' }}>
                  {loadingBranding ? 'Saving...' : 'Save Changes'}
                </Text>
              </Button>
            </XStack>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog>
    </DefaultScreen >
  )
}
export default DataTemplates;

